import type { UseQueryResult } from '@tanstack/react-query'
import { replaceEqualDeep, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type {
  GetContentResponse,
  GetResourcesResponse,
  GetStepsContentResponse,
  GetStepsResponse,
  ProcessProgress,
  ResourceType,
} from '@/services/api/api.types'
import type { ThumbnailSource } from '@/utils/thumbnails'
import { resolveThumbnailUrl } from '@/utils/thumbnails'

export type StepWithValidation = GetStepsResponse[0] & {
  valid: boolean
  /**
   * No status has been loaded for this step yet, so the status fields below are
   * absent rather than false. Distinguishes "not transcoding" from "unknown",
   * which otherwise both render as ready.
   */
  statusPending?: boolean
  contentType?: ResourceType
  // Resolved image URL for the source file's thumbnail. Drives the table's thumbnail column preview.
  thumbnailUrl?: string | null
  // Video title the backend pulled from YouTube oEmbed. Only set for `youtube` source files.
  youtubeTitle?: string | null
  inProcess?: boolean | null
  processProgress?: ProcessProgress['processProgress']
  processProgressError?: string
  transcodeCompleted?: boolean
  transcodeSuccess?: boolean
  /**
   * The source file's resource, narrowed to what the table's Content Access control
   * needs — its own id to write against, and the two facts that decide the options.
   */
  sourceResourceId?: string | null
  showInApp?: boolean | null
  showInAppSupported?: boolean | null
}

type StepStatus = {
  inProcess?: boolean
  transcodeCompleted?: boolean
  transcodeSuccess?: boolean
  processProgress?: ProcessProgress['processProgress']
}

// Narrowed from `/cm/steps_content`, which returns ~4.8MB for a 562-step module —
// mostly transcript text and re-signed URLs the table never reads.
type StepContentSummary = StepStatus & {
  contentType?: ResourceType
  thumbnail: ThumbnailSource | null
  youtubeTitle: string | null
  sourceResourceId: string | null
  showInApp: boolean | null
  showInAppSupported: boolean | null
}

type StepContentSummaries = Record<string, StepContentSummary>

const combineStatusResults = (results: UseQueryResult[]): Array<StepStatus | undefined> =>
  results.map(r => r.data as StepStatus | undefined)

// Stable identities for the pre-success paths; consumers key effects off `data`'s reference.
const EMPTY_STEPS: GetStepsResponse = []
const EMPTY_VALIDATED_STEPS: StepWithValidation[] = []
const EMPTY_SUMMARIES: StepContentSummaries = {}

const MAX_STEPS_TO_PATCH = 10
const MAX_PATCH_ATTEMPTS = 3

const summarizeStep = (
  content: GetContentResponse | null | undefined,
  resources: GetResourcesResponse | undefined,
): StepContentSummary => {
  const sourceFile = resources?.find(resource => resource.isSourceFile)

  return {
    inProcess: content?.inProcess,
    transcodeCompleted: content?.transcodeCompleted,
    transcodeSuccess: content?.transcodeSuccess,
    processProgress: content?.processProgress,
    contentType: sourceFile?.type,
    youtubeTitle: sourceFile?.type === 'youtube' ? sourceFile.title?.trim() || null : null,
    sourceResourceId: sourceFile?.resourceId ?? null,
    showInApp: sourceFile?.showInApp ?? null,
    showInAppSupported: sourceFile?.showInAppSupported ?? null,
    // `data` can hold a whole extracted document; only the YouTube still needs it.
    thumbnail: sourceFile
      ? {
          type: sourceFile.type,
          thumbnailS3Key: sourceFile.thumbnailS3Key,
          youtubeId: sourceFile.youtubeId,
          data: sourceFile.type === 'youtube' ? sourceFile.data : null,
          sourceUrl: sourceFile.type === 'youtube' ? sourceFile.sourceUrl : null,
        }
      : null,
  }
}

const summarizeStepsContent = (response: GetStepsContentResponse): StepContentSummaries => {
  const summaries: StepContentSummaries = {}

  for (const step of response.steps) {
    summaries[step.stepId] = summarizeStep(step.content, step.resources)
  }

  return summaries
}

// Signatures measured at ~24h, so re-resolve well inside that.
const THUMBNAIL_URL_TTL = 12 * 60 * 60 * 1000

// `thumbnailS3Key` is re-signed on every response, so resolving it directly gives
// every row a new identity on refetch. Cache on the unsigned path instead.
const resolveStableThumbnailUrl = (
  thumbnail: ThumbnailSource | null | undefined,
  serverBaseUrl: string | undefined,
  cache: Map<string, { url: string; resolvedAt: number }>,
) => {
  if (!thumbnail) return null
  // YouTube stills derive from the video id, already stable.
  if (!thumbnail.thumbnailS3Key) return resolveThumbnailUrl(thumbnail, serverBaseUrl)

  const path = thumbnail.thumbnailS3Key.split('?')[0]
  const cached = cache.get(path)
  if (cached && Date.now() - cached.resolvedAt < THUMBNAIL_URL_TTL) return cached.url

  const url = resolveThumbnailUrl(thumbnail, serverBaseUrl)
  // Not cached when null: `serverBaseUrl` may simply not be populated yet.
  if (url) cache.set(path, { url, resolvedAt: Date.now() })
  return url
}

const statusPollInterval = (query: { state: { data?: unknown } }) => {
  const data = query.state.data as StepStatus | undefined
  if (data?.processProgress?.error) return false
  if (data && (data.inProcess === true || data.transcodeCompleted === false)) return 5000
  return false
}

export type StepsRefetchOptions = {
  /** The step that changed, so its status and source file refresh. Omit for ordering-only changes. */
  stepId?: string
}

export interface StepsWithValidationResult {
  data: StepWithValidation[]
  isLoading: boolean
  isError: boolean
  /** The steps list itself failed, so the module's files are unknown rather than absent. */
  isStepsError: boolean
  error: Error | null
  isSuccess: boolean
  /** The status snapshot failed, so every row's status is unknown until it is retried. */
  isStatusError: boolean
  /** The status snapshot landed, so `contentType` is trustworthy. False while loading and after a failure. */
  isStatusReady: boolean
  refetch: (options?: StepsRefetchOptions) => Promise<{ data?: StepWithValidation[] }>
  /**
   * Refetch the status snapshot after a failure. `retry: 1` and `staleTime: Infinity` mean
   * nothing else will, and until this existed a single failed request cost the expert
   * Content Access and module generation for the rest of the session.
   */
  retryStatuses: () => Promise<void>
}

/**
 * Joins the base step list with each step's content status and source file.
 *
 * C2-2501: this used to issue `/content/get` plus `/cm/get_resources` per step,
 * which was 1,124 requests to open a 562-step module. One `/cm/steps_content`
 * call replaces them, and is never polled — re-sending several MB every 5s would
 * be worse than the fan-out. Only steps mid-upload or mid-transcode get a poller.
 */
export function useStepsWithValidation(
  stepsQuery: {
    data?: GetStepsResponse
    isSuccess: boolean
    isLoading: boolean
    isError: boolean
    error: Error | null
    refetch: () => Promise<{ data?: GetStepsResponse }>
  },
  taskId: string | undefined,
): StepsWithValidationResult {
  const { api, configStore } = useGlobalState()
  const queryClient = useQueryClient()
  const serverBaseUrl = configStore.serverBaseUrl

  // Backs the replaceEqualDeep structural sharing on the memo output below.
  const sharedStepsRef = useRef<StepWithValidation[]>([])
  const thumbnailUrlCacheRef = useRef(new Map<string, { url: string; resolvedAt: number }>())

  const steps = stepsQuery.data ?? EMPTY_STEPS

  const stepsContentQuery = useQuery({
    queryKey: ['cm', 'stepsContent', taskId],
    queryFn: async () => summarizeStepsContent(await api.getStepsContent({ task_id: taskId! })),
    // Behind the steps request so a locked module doesn't pull several MB first.
    enabled: !!taskId && stepsQuery.isSuccess,
    // Several MB per fetch: fetched once, then kept current by patching steps.
    // Defaults would re-pull it on every window focus and retry it 3x on a 5xx.
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    notifyOnChangeProps: ['data', 'error', 'isSuccess'] as Array<keyof UseQueryResult>,
  })

  const summaries = stepsContentQuery.data ?? EMPTY_SUMMARIES

  // Mount list only: each query's refetchInterval stops itself, so poll results
  // never feed back into this list and remount it.
  const pollingStepIds = useMemo(
    () =>
      steps
        .filter(step => {
          const summary = summaries[step.stepId]
          return step.inProcess === true || summary?.inProcess === true || summary?.transcodeCompleted === false
        })
        .map(step => step.stepId),
    [steps, summaries],
  )

  const statusResults = useQueries({
    queries: pollingStepIds.map(stepId => ({
      queryKey: ['step', 'content', 'meta', stepId],
      queryFn: () => api.getContentMeta({ step_id: stepId }),
      staleTime: 4000,
      // Retained status let a remounted poller serve a stale "done" over a fresher summary.
      gcTime: 0,
      // Pollers stay mounted after their interval stops, so this would fire one
      // request per finished step on every focus.
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['data', 'error', 'isSuccess'] as Array<keyof UseQueryResult>,
      refetchInterval: statusPollInterval,
    })),
    combine: combineStatusResults,
  })

  const liveStatuses = useMemo(() => {
    const byStepId: Record<string, StepStatus> = {}
    statusResults.forEach((status, index) => {
      const stepId = pollingStepIds[index]
      if (stepId && status) byStepId[stepId] = status
    })
    return byStepId
  }, [statusResults, pollingStepIds])

  // replaceEqualDeep collapses deeply-equal rebuilds back to the prior reference
  // (and reuses unchanged row refs) to avoid a full table reconcile each render.
  const stepsWithValidation = useMemo(() => {
    if (!stepsQuery.isSuccess) {
      return EMPTY_VALIDATED_STEPS
    }

    const built = steps.map(step => {
      const summary = summaries[step.stepId]
      // A live poll is fresher than the bulk snapshot whenever one is running.
      const status: StepStatus | undefined = liveStatuses[step.stepId] ?? summary

      const processProgress = status?.processProgress
      const rawError = processProgress?.error

      return {
        ...step,
        valid: !!step.name,
        statusPending: !summary && !liveStatuses[step.stepId],
        contentType: summary?.contentType,
        sourceResourceId: summary?.sourceResourceId,
        showInApp: summary?.showInApp,
        showInAppSupported: summary?.showInAppSupported,
        thumbnailUrl: resolveStableThumbnailUrl(summary?.thumbnail, serverBaseUrl, thumbnailUrlCacheRef.current),
        youtubeTitle: summary?.youtubeTitle ?? null,
        inProcess: step.inProcess === true || status?.inProcess === true,
        processProgress,
        processProgressError: typeof rawError === 'string' ? rawError : rawError?.message,
        transcodeCompleted: status?.transcodeCompleted,
        transcodeSuccess: status?.transcodeSuccess,
      }
    })

    const shared = replaceEqualDeep(sharedStepsRef.current, built) as StepWithValidation[]
    sharedStepsRef.current = shared
    return shared
  }, [steps, summaries, liveStatuses, stepsQuery.isSuccess, serverBaseUrl])

  // Stable refetch function that doesn't change reference across renders.
  // All mutable dependencies are accessed via refs so the closure never goes stale.
  const refetchStepsRef = useRef(stepsQuery.refetch)
  refetchStepsRef.current = stepsQuery.refetch
  const taskIdRef = useRef(taskId)
  taskIdRef.current = taskId
  const apiRef = useRef(api)
  apiRef.current = api

  /**
   * Refreshes named steps in place. The snapshot is never refetched, so this is the
   * only thing keeping it current — two small requests instead of several MB.
   */
  const patchSteps = useRef(async (stepIds: string[]) => {
    const taskId = taskIdRef.current
    if (!taskId || !stepIds.length) return

    // Display data only, so a failure must not fail the save that triggered it.
    try {
      // Past a handful, the per-step pair is the fan-out again. `cancelRefetch: false`
      // joins an in-flight fetch instead of restarting it, so overlapping fallbacks
      // can't stack several MB downloads.
      if (stepIds.length > MAX_STEPS_TO_PATCH) {
        await queryClient.invalidateQueries(
          { queryKey: ['cm', 'stepsContent', taskId], exact: true },
          { cancelRefetch: false },
        )
        return
      }

      const patched = await Promise.all(
        stepIds.map(async stepId => {
          const [content, resources] = await Promise.all([
            apiRef.current.getContentMeta({ step_id: stepId }),
            // Raw request, not `getResources`, which also downloads each file.
            apiRef.current.request<GetResourcesResponse>('/cm/get_resources', 'get', { step_id: stepId }),
          ])
          return [stepId, summarizeStep(content, resources)] as const
        }),
      )

      // Deliberately not cancelling an in-flight snapshot fetch first: cancellation
      // reverts, which on the initial fetch strands the query pending forever with
      // nothing to restart it. A landing snapshot is newer than this patch anyway.
      queryClient.setQueryData<StepContentSummaries>(['cm', 'stepsContent', taskId], prev =>
        prev ? { ...prev, ...Object.fromEntries(patched) } : prev,
      )

      // This patch is newer than any cached poll, and a poller that already reported
      // "done" has stopped its own interval — so without this, replacing the file on
      // a finished step would keep reading the previous transcode as complete.
      stepIds.forEach(stepId =>
        queryClient.removeQueries({ queryKey: ['step', 'content', 'meta', stepId], exact: true }),
      )
    } catch (error) {
      console.error('[useStepsWithValidation] Failed to refresh steps:', stepIds, error)
    }
  }).current

  /**
   * One list fetch at a time, with a single follow-up for whatever settled while it was in
   * flight. `refetch` defaults to `cancelRefetch: true`, so a bulk upload whose files
   * settled within a request of each other aborted `/step/task_steps` over and over,
   * throwing away a full response each time on exactly the modules this fan-out exists for.
   *
   * Coalesced rather than debounced: no delay on the usual single settle, no window in
   * which a stream of settles keeps postponing the fetch, and the follow-up starts after
   * the settles that asked for it — so unlike joining an in-flight fetch, it cannot answer
   * with a list that predates them.
   */
  const listRefreshRef = useRef({ running: false, pending: false })
  const refreshStepsList = useRef(async () => {
    listRefreshRef.current.pending = true
    if (listRefreshRef.current.running) return

    listRefreshRef.current.running = true
    try {
      while (listRefreshRef.current.pending) {
        listRefreshRef.current.pending = false
        await refetchStepsRef.current()
      }
    } finally {
      listRefreshRef.current.running = false
    }
  }).current

  const refetchStatusesRef = useRef(stepsContentQuery.refetch)
  refetchStatusesRef.current = stepsContentQuery.refetch
  const retryStatuses = useRef(async () => {
    await refetchStatusesRef.current()
  }).current

  const refetch = useRef(async (options?: StepsRefetchOptions) => {
    const stepsResult = await refetchStepsRef.current()

    if (options?.stepId) {
      await patchSteps([options.stepId])
    }

    if (stepsResult.data) {
      const enhancedSteps = stepsResult.data.map(step => ({
        ...step,
        valid: !!step.name,
      }))
      return { data: enhancedSteps }
    }

    return { data: undefined }
  }).current

  // Steps the snapshot doesn't cover are fresh uploads, and would render with no type
  // or thumbnail. Counted per id so a transient failure gets another chance while one
  // the endpoint never returns is eventually dropped.
  const missingAttemptsRef = useRef(new Map<string, number>())
  useEffect(() => {
    if (!stepsContentQuery.isSuccess) return

    const attempts = missingAttemptsRef.current
    const missing = steps
      .filter(step => !summaries[step.stepId] && (attempts.get(step.stepId) ?? 0) < MAX_PATCH_ATTEMPTS)
      .map(step => step.stepId)
    if (!missing.length) return

    missing.forEach(stepId => attempts.set(stepId, (attempts.get(stepId) ?? 0) + 1))
    patchSteps(missing)
  }, [steps, summaries, stepsContentQuery.isSuccess, patchSteps])

  // Transcoding populates the thumbnail and content type, but pollers carry only
  // status, so without this a row goes green with an empty thumbnail cell. Patching
  // on a transition rather than on "is done" keeps a module that opens with many
  // unfinished steps from refreshing all of them at once.
  const pendingTranscodeRef = useRef(new Set<string>())
  const settledPatchRef = useRef(new Set<string>())
  useEffect(() => {
    if (!stepsContentQuery.isSuccess) return

    // The snapshot's own in-process flags seed the transition.
    Object.entries(summaries).forEach(([stepId, summary]) => {
      if (summary.inProcess === true || summary.transcodeCompleted === false) {
        pendingTranscodeRef.current.add(stepId)
      }
    })

    const settled: string[] = []
    Object.entries(liveStatuses).forEach(([stepId, status]) => {
      if (status.inProcess === true || status.transcodeCompleted === false) {
        pendingTranscodeRef.current.add(stepId)
        // Re-arm: a replaced file transcodes again and needs its new thumbnail. Keyed
        // off the live poll, not the summary, so a patch can't re-arm its own step.
        settledPatchRef.current.delete(stepId)
        return
      }
      // Patch once per cycle: the patch itself changes `summaries` and re-runs this.
      if (pendingTranscodeRef.current.delete(stepId) && !settledPatchRef.current.has(stepId)) {
        settledPatchRef.current.add(stepId)
        settled.push(stepId)
      }
    })

    if (settled.length) {
      // The steps list itself carries `name` and `description`, and processing writes them
      // in the same pass that clears `in_process`. Patching only the snapshot refreshes the
      // status without the metadata, so a file that has finished sits as "Untitled" and
      // "Review Needed" until something unrelated refetches the list.
      refreshStepsList()
      patchSteps(settled)
    }
  }, [liveStatuses, summaries, stepsContentQuery.isSuccess, patchSteps, refreshStepsList])

  // The route is shared between modules, so the component can survive a switch.
  useEffect(() => {
    return () => {
      missingAttemptsRef.current.clear()
      pendingTranscodeRef.current.clear()
      settledPatchRef.current.clear()
      thumbnailUrlCacheRef.current.clear()
    }
  }, [taskId])

  // Only recompute result when data, loading, or error state actually changes.
  // Background refetch toggles (isFetching) are intentionally excluded — each
  // query's isFetching toggle was causing a full tree re-render with many steps.
  const result = useMemo((): StepsWithValidationResult => {
    return {
      data: stepsWithValidation,
      isLoading: stepsQuery.isLoading,
      isError: stepsQuery.isError || stepsContentQuery.isError,
      isStepsError: stepsQuery.isError,
      error: stepsQuery.error || stepsContentQuery.error,
      isSuccess: stepsQuery.isSuccess,
      isStatusError: stepsContentQuery.isError,
      isStatusReady: stepsContentQuery.isSuccess,
      refetch,
      retryStatuses,
    }
  }, [
    stepsWithValidation,
    stepsQuery.isLoading,
    stepsQuery.isError,
    stepsQuery.error,
    stepsQuery.isSuccess,
    stepsContentQuery.isError,
    stepsContentQuery.isSuccess,
    stepsContentQuery.error,
    refetch,
    retryStatuses,
  ])

  return result
}
